import { flushQueue, type FlushResult } from './grade-queue';

/**
 * Sync status — driver dla `NetworkStatusStrip`. `lastError` tylko gdy `kind = 'error'`.
 */
export type SyncStatus =
  | { kind: 'idle' }
  | { kind: 'flushing'; pending: number }
  | { kind: 'error'; reason: 'syncError' | 'authFailure' };

export interface SyncControllerDeps {
  getAccessTokenSilently: () => Promise<string>;
  /** Wywołane gdy 401 + silent refresh sfailowal — main.tsx redirectuje do /login. */
  onAuthFailure: () => void;
  /** Wywołane po flush z deletedCardIds (dla toast). */
  onDeletedCards?: (cardIds: number[]) => void;
}

const HEARTBEAT_MS = 5 * 60 * 1000;
const POST_REFRESH_RETRY_DELAY_MS = 1000;

let initialized = false;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let onlineListener: ((this: Window, ev: Event) => unknown) | null = null;
let currentDeps: SyncControllerDeps | null = null;
let currentStatus: SyncStatus = { kind: 'idle' };
const subscribers = new Set<(s: SyncStatus) => void>();

function setStatus(next: SyncStatus): void {
  currentStatus = next;
  subscribers.forEach((s) => s(next));
}

export function getSyncStatus(): SyncStatus {
  return currentStatus;
}

export function subscribeSyncStatus(cb: (s: SyncStatus) => void): () => void {
  subscribers.add(cb);
  cb(currentStatus);
  return () => {
    subscribers.delete(cb);
  };
}

export async function triggerFlushNow(): Promise<void> {
  // Capture deps locally — disposeSyncController() can null `currentDeps` between awaits
  // (e.g. Auth0 token rotation triggers main.tsx useEffect cleanup→re-init mid-flush).
  // Without local capture, accessing `currentDeps.onAuthFailure` after `await flushQueue()`
  // crashes with "Cannot read properties of null". Treat dispose as graceful: in-flight
  // flush completes with stale callbacks rather than crashes.
  const deps = currentDeps;
  if (!deps) return;
  setStatus({ kind: 'flushing', pending: 0 });
  let result: FlushResult;
  try {
    result = await flushQueue();
  } catch (err) {
    console.warn('[sync-controller] unexpected flushQueue exception', err);
    setStatus({ kind: 'error', reason: 'syncError' });
    return;
  }

  if (result.deletedCardIds.length > 0 && deps.onDeletedCards) {
    deps.onDeletedCards(result.deletedCardIds);
  }

  if (result.authRefreshNeeded) {
    try {
      await deps.getAccessTokenSilently();
    } catch {
      deps.onAuthFailure();
      setStatus({ kind: 'error', reason: 'authFailure' });
      return;
    }
    await new Promise((r) => setTimeout(r, POST_REFRESH_RETRY_DELAY_MS));
    const retry = await flushQueue();
    if (retry.syncError) {
      setStatus({ kind: 'error', reason: 'syncError' });
      return;
    }
    if (retry.deletedCardIds.length > 0 && deps.onDeletedCards) {
      deps.onDeletedCards(retry.deletedCardIds);
    }
  }

  if (result.syncError) {
    setStatus({ kind: 'error', reason: 'syncError' });
    return;
  }

  setStatus({ kind: 'idle' });
}

export function initSyncController(deps: SyncControllerDeps): void {
  if (initialized) return;
  initialized = true;
  currentDeps = deps;

  onlineListener = () => {
    void triggerFlushNow();
  };
  window.addEventListener('online', onlineListener);

  heartbeatTimer = setInterval(() => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    void triggerFlushNow();
  }, HEARTBEAT_MS);

  if (typeof navigator === 'undefined' || navigator.onLine) {
    void triggerFlushNow();
  }
}

export function disposeSyncController(): void {
  if (onlineListener) {
    window.removeEventListener('online', onlineListener);
    onlineListener = null;
  }
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  initialized = false;
  currentDeps = null;
  currentStatus = { kind: 'idle' };
  // Subscriber lifecycle is owned by consumers (their useEffect cleanup deletes their
  // own callback via the unsubscribe returned from subscribeSyncStatus). Clearing the
  // set here would orphan still-mounted consumers — after init→dispose→re-init cycle
  // (StrictMode dev double-mount, login→logout→login w jednej SPA sesji), consumer's
  // [] deps useEffect doesn't re-subscribe, so strip would never get future status updates.
}
